import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// store
import { useSelector } from '../../../store';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import TableInfo from '../../../components/table-info';
import Pagination from '../../../components/pagination';
import Dialog from '../../../components/dialog';
// @devextreme
import DateBox from 'devextreme-react/date-box';
import DateRangeBox from 'devextreme-react/date-range-box';
import { Template } from 'devextreme-react/core/template';
import { Popover } from 'devextreme-react/popover';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
// @devextreme -- export to excel
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
// assets
import { ReactComponent as Download } from '../../../assets/button/download.svg';
import { ReactComponent as Settings } from '../../../assets/button/settings.svg';
import Calendar from '../../../assets/button/Calendar';
// styles
import { Container, SearchWrapper, Tabs, Box, PrimaryButton, IconButton, DateButton, MobileDateWrapper } from '../../../styles';
// apis
import { getAlarms, downloadAlarms } from '../../../apis';

// ----------------------------------------------------------------------

const now = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
const intervals = [{ value: 'daily', name: '일간' }, { value: 'weekly', name: '주간' }, { value: 'monthly', name: '월간' }, { value: 'setting', name: '직접 설정' }];
const sizes = [10, 20, 30];

// ----------------------------------------------------------------------

export default function AlarmPage() {
  const navigate  = useNavigate();

  const mobile = useResponsive('down', 'md');

  const group = useSelector((state) => state.group);

  const [ interval, setInterval ] = useState('daily');
  const [ display, setDisplay ] = useState('yyyy-MM-dd');

  const [ saveStart, setSaveStart ] = useState<any>({ daily: new Date(now), weekly: new Date(now), monthly: new Date(now), setting: new Date(now) });
  const [ saveEnd, setSaveEnd ] = useState<any>({ daily: new Date(now), weekly: new Date(now), monthly: new Date(now), setting: new Date(now) });

  const [ start, setStart ] = useState(new Date(now));
  const [ end, setEnd ] = useState(new Date(now));
  const [ range, setRange ] = useState(false);

  const [ target, setTarget ] = useState('location');
  const [ keyword, setKeyword ] = useState('');
  const [ page, setPage ] = useState(0);
  const [ size, setSize ] = useState(20);
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ searched, setSearched ] = useState(null);
  
  const [ visible, setVisible ] = useState(false);

  const [ message, setMessage ] = useState(null);
  const handleClose = () => setMessage(null);

  useEffect(() => {
    if ( group.id ) handleSearch();
  }, [ group.id ]);

  useEffect(() => {
    setSaveStart({ ...saveStart, [interval]: start});
  }, [start]);

  useEffect(() => {
    setSaveEnd({ ...saveEnd, [interval]: end});
  }, [end]);

  useEffect(() => {
    if ( interval === 'weekly' ) {
      handleWeekly(saveStart.weekly);
    } else {
      setStart(saveStart[interval]);
      setEnd(saveEnd[interval]);
    }
    
    if ( interval === 'monthly' ) {
      document.body.className  += ' active';
    } else {
      document.body.classList.remove('active');
    }

    switch ( interval ) {
      case 'monthly':
        return setDisplay('yyyy-MM');
      default:
        return setDisplay('yyyy-MM-dd');
    }
  }, [interval]);

  const handleWeekly = (value: Date) => {
    const current = new Date(value);
    const startDate = new Date(current.setDate(current.getDate() - current.getDay()));
    const sunday = new Date(startDate);
    let endDate = new Date(sunday.setDate(sunday.getDate() + 6));
    if ( endDate.getTime() > new Date(now).getTime() ) endDate = new Date(now);

    setStart(new Date(startDate.getTime()));
    setEnd(new Date(endDate.getTime()));
  };

  const onValueChange = (event: any) => {
    if ( !mobile ) {
      const previous = event.previousValue;
      const values = event.value;
      const filter = values.filter((value: string) => !previous.includes(value));
      const value = filter.length > 0 ? filter[0] : null;
  
      const startDate = new Date(previous[0]).getTime();
      const endDate = new Date(previous[1]).getTime();
      const currentDate = new Date(value).getTime();
  
      if ( !value || ( currentDate >= startDate && currentDate <= endDate ) ) return;
  
      setRange(false);
      handleWeekly(value);
      document.getElementById('rangeStart').blur();
      setTimeout(() => {
        document.getElementById('rangeEnd').blur();
      }, 5);
    } else {
      handleWeekly(event.value);
    }
  };

  const search = () => {
    return {
      location: target === 'location' ? keyword : '',
      waterQuality: target === 'waterQuality' ? keyword : ''
    }
  };

  const handlePage = (value: number) => {
    setPage(value-1);
    handleSearch({page: value-1, size});
  };

  const handleSize = (value: number) => {
    setPage(0);
    setSize(value);
    handleSearch({page: 0, size: value});
  };

  const handleSearch = (paging?: { page: number, size: number }) => {
    if ( !start || !end ) return;
    
    if ( interval === 'weekly' || interval === 'setting' ) {
      let difference = Math.abs( start.getTime() - end.getTime() );
      difference = Math.ceil( difference / (1000 * 60 * 60 * 24) );

      if ( difference > 90 ) return setMessage('90일 이내로 검색해주세요.');
      
      if ( start > end ) return setMessage('검색 시작 시간은 종료 시간보다 이전이어야 합니다.\n검색 기간을 다시 한번 확인해 주세요.');
    }
    if ( moment(end).isAfter(now, 'day') ) return;
    if ( keyword.length > 0 && keyword.replace(/ /g, '').length === 0 ) return;

    if ( !paging ) setPage(0);
    getAlarms(
      setData, group.id,
      paging ? searched.start : start,
      paging ? searched.end : (interval === 'daily' || interval === 'monthly') ? start : end,
      paging ? searched.interval : interval,
      paging ? searched.search : search(),
      paging ? { page: paging.page, size: paging.size } : { page: 0, size }
    )
    .then(() => setSearched({
      interval: paging ? searched.interval : interval,
      start: paging ? searched.start : start,
      end: paging ? searched.end : (interval === 'daily' || interval === 'monthly') ? start : end,
      search: paging ? searched.search : search()
    }))
    .catch(() => {
      setSearched(null);
      setData({ content: [], totalElements: 0, totalPages: 1 });
    });
  };

  const gridRef = useRef(null);

  const handleDownload = async (value: string) => {
    if ( value === 'current' ) {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      exportDataGrid({
        worksheet,
        component: gridRef.current.instance
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `alarm_data-${timezones(Date.now(), 'YYYYMMDDHHmmss')}.csv`);
        });
      });
    } else if ( value === 'all' ) {
        await downloadAlarms(group.id, searched.start, searched.end, searched.interval, searched.location, searched.waterQuality)
          .catch(() => setMessage('다운로드를 할 수 없습니다.\n나중에 다시 시도해 주세요.'));
    }
  };

  const handleDate = (type: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    switch ( interval ) {
      case 'daily':
        if ( type !== 'previous' && moment(startDate).isSameOrAfter(now, 'day') ) return;
        const date = type === 'previous' ? startDate.setDate(startDate.getDate() - 1) : startDate.setDate(startDate.getDate() + 1);
        return setStart(new Date(date));
      case 'weekly':
        const date2 = type === 'previous' ? startDate.setDate(startDate.getDate() - 1) : endDate.setDate(endDate.getDate() + 1);
        return handleWeekly(new Date(date2));
      default:
        if ( type !== 'previous' && moment(startDate).isSameOrAfter(now, 'month') ) return;
        const date3 = type === 'previous' ? startDate.setMonth(startDate.getMonth() - 1) : startDate.setMonth(startDate.getMonth() + 1);
        setStart(new Date(date3));
    }
  };

  const cellRender = ({data}: any) => {
    return timezones(data.alarmAt, 'YYYY-MM-DD HH:mm:ss');
  };

  return (
    <Container>
      <Breadcrumbs heading='알람' />

      <SearchWrapper>
        <div>
          <Tabs>
            { intervals.map((item) => (
              <button key={item.value} onClick={() => setInterval(item.value)} className={interval === item.value ? 'active' : ''}>{item.name}</button>
            ))}
          </Tabs>

          <MobileDateWrapper>
            { interval !== 'setting' &&
              <DateButton onClick={() => handleDate('previous')} />
            }

            { interval === 'weekly' && !mobile &&
              <DateRangeBox
                opened={range}
                value={[start, end]}
                onValueChanged={onValueChange}
                onFocusIn={() => setRange(true)}
                onFocusOut={() => setRange(false)}
                displayFormat={display}
                openOnFieldClick={true}
                useMaskBehavior={true}
                startDateLabel=''
                endDateLabel=''
                startDateInputAttr={{id: 'rangeStart'}}
                endDateInputAttr={{id: 'rangeEnd'}}
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateRangeBox>
            }

            { interval === 'weekly' && mobile &&
              <div className='rangeBox'>
                <DateBox
                  type='date'
                  value={start}
                  onValueChanged={onValueChange}
                  displayFormat={display}
                  elementAttr={{class: 'calendarStart'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                />
                <p className='range'>~</p>
                <DateBox
                  type='date'
                  value={end}
                  onValueChanged={onValueChange}
                  displayFormat={display}
                  elementAttr={{class: 'calendarEnd'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                  dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateBox>
              </div>
            }

            { interval !== 'weekly' && interval !== 'setting' &&
              <DateBox
                type={ interval === 'setting' ? 'datetime' : 'date' }
                value={start}
                onValueChanged={(event) => setStart(event.value)}
                displayFormat={display}
                calendarOptions={{
                  maxZoomLevel: interval === 'monthly' ? 'year' : 'month',
                  minZoomLevel: 'century'
                }}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType={mobile ? 'rollers' : 'calendar'}
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateBox>
            }

            { interval !== 'setting' &&
              <DateButton onClick={() => handleDate('next')} $next />
            }

            { interval === 'setting' && !mobile &&
              <DateRangeBox
                value={[start, end]}
                onValueChanged={(event) => {
                  setStart(event.value[0]);
                  setEnd(event.value[1]);
                }}
                displayFormat='yyyy-MM-dd'
                openOnFieldClick={true}
                useMaskBehavior={true}
                startDateLabel=''
                endDateLabel=''
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateRangeBox>
            }

            { interval === 'setting' && mobile &&
              <div className='rangeBox' style={{'width': '100%'}}>
                <DateBox
                  type='date'
                  value={start}
                  onValueChanged={(event) => setStart(event.value)}
                  displayFormat={display}
                  elementAttr={{class: 'calendarStart'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                />
                <p className='range'>~</p>
                <DateBox
                  type='date'
                  value={end}
                  onValueChanged={(event) => setEnd(event.value)}
                  displayFormat={display}
                  elementAttr={{class: 'calendarEnd'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                  dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateBox>
              </div>
            }
          </MobileDateWrapper>

          <select value={target} onChange={(event) => setTarget(event.target.value)}>
            <option value='location'>설치 위치</option>
            <option value='waterQuality'>수질 항목</option>
          </select>

          <input type='text' placeholder='검색어를 입력하세요.' onChange={(event) => setKeyword(event.target.value)} onKeyDown={(event) => { if(event.key === 'Enter') handleSearch() }} />

          <PrimaryButton type='button' onClick={() => handleSearch()} disabled={!group.id? true : false}>검색</PrimaryButton>
        </div>

        <div>
          <IconButton type='button' id='download' onClick={() => setVisible(true)} disabled={!group.id? true : false}><Download />다운로드</IconButton>
          <IconButton type='button' onClick={() => navigate('/alarm-settings')}><Settings />알람 설정</IconButton>
        </div>
      </SearchWrapper>
      <Popover
        target='#download'
        visible={visible}
        hideOnOutsideClick={true}
        onHiding={() => setVisible(false)}
      >
        <button type='button' onClick={() => handleDownload('current')}>현재 페이지</button>
        <button type='button' onClick={() => handleDownload('all')}>원본 데이터</button>
      </Popover>
      
      <Box>
        <TableInfo total={total} pages={pages} page={page} sizes={sizes} size={size} handleSize={handleSize} />

        <DataGrid
          ref={gridRef}
          dataSource={rows}
          noDataText=''
          columnAutoWidth={true}
          paging={{enabled: false}}
        >
          <Column caption='시간' dataField='alarmAt' alignment='center' cellRender={cellRender} />
          <Column caption='설치 위치' dataField='location' alignment='center' />
          <Column caption='수질 항목' dataField='waterQuality' alignment='center' />
          <Column caption='알람 값' dataField='alarmValue' alignment='center' />
          <Column caption='비고' dataField='alarm' alignment='center' />
        </DataGrid>

        <Pagination page={page} pages={pages} size={size} handlePage={handlePage} />
      </Box>

      { message &&
        <Dialog
          handleClose={handleClose}
        >
          <p>{ message }</p>
          
          <PrimaryButton type='button' onClick={() => handleClose()} style={{'marginTop': '40px'}}>확인</PrimaryButton>

        </Dialog>
      }
    </Container>
  );
}